import { useClamp } from '@/util/math'
import { getISODateString, useISODateAsDate, UseISODateAsDateOptions } from '@/util/time'
import { useDebounceFn } from '@vueuse/core'
import { useRouteQuery } from '@vueuse/router'
import { computed, MaybeRefOrGetter, nextTick, toValue, watchEffect } from 'vue'
import { RouteLocationRaw, RouteParamValueRaw, useRouter } from 'vue-router'

export function useRedirectIfNotFound(
  redirectRoute: MaybeRefOrGetter<RouteLocationRaw>,
  ...preconditions: [
    object: MaybeRefOrGetter<unknown | null>,
    isLoading: MaybeRefOrGetter<boolean>,
  ][]
) {
  const router = useRouter()

  watchEffect(async () => {
    if (preconditions.length === 0) return

    // If any of the objects are still loading, we shouldn’t do anything yet.
    if (preconditions.some(([, isLoading]) => toValue(isLoading))) return

    // Objects aren’t loading anymore, but the object getters might not yet be resolved
    // so we give them a single tick.
    await nextTick()

    const objects = preconditions.map(([obj]) => toValue(obj))
    if (objects.some((obj) => obj === null)) {
      // An object is null even though it’s not loading anymore.
      // Time to redirect!
      await router.push(toValue(redirectRoute))
    }
  })
}

type DefaultQueryOptions = {
  prefix?: string | undefined
}

type UsePrefixedRouteQueryOptions<
  T extends RouteParamValueRaw = RouteParamValueRaw,
  K = T,
> = DefaultQueryOptions & Parameters<typeof useRouteQuery<T, K>>['2']

export function usePrefixedRouteQuery<T extends RouteParamValueRaw = RouteParamValueRaw, K = T>(
  key: string,
  value: MaybeRefOrGetter<T>,
  options: UsePrefixedRouteQueryOptions<T, K> | undefined = undefined,
) {
  const _prefix = options?.prefix ? `${options.prefix}.` : ''
  const _key = `${_prefix}${key}`
  return useRouteQuery(_key, value, options)
}

export function usePageParameter(
  options: UsePrefixedRouteQueryOptions<number> | undefined = undefined,
) {
  return useClamp(usePrefixedRouteQuery('page', 1, { ...options, transform: Number }), {
    min: 1,
    sync: true,
  })
}

type UseLimitParameterOptions = UsePrefixedRouteQueryOptions<number> & {
  maxLimit?: number | undefined
}

export function useLimitParameter(
  defaultLimit: MaybeRefOrGetter<number>,
  options: UseLimitParameterOptions | undefined = undefined,
) {
  const maxLimit = options?.maxLimit ?? 100
  return useClamp(usePrefixedRouteQuery('limit', defaultLimit, { ...options, transform: Number }), {
    min: 1,
    max: maxLimit,
    sync: true,
  })
}

type UseDateParameterOptions = UsePrefixedRouteQueryOptions<string> & UseISODateAsDateOptions

export function useDateParameter(
  key: string,
  options: UseDateParameterOptions | undefined = undefined,
) {
  return useISODateAsDate(
    usePrefixedRouteQuery(key, getISODateString(), { prefix: options?.prefix }),
    options,
  )
}

type UseSearchParameterOptions = UsePrefixedRouteQueryOptions<string> & {
  debounce?: MaybeRefOrGetter<number> | undefined
}

export function useSearchParameter(
  key: string = 'search',
  options: UseSearchParameterOptions | undefined = undefined,
) {
  const routeParameter = usePrefixedRouteQuery(key, '', options)
  return computed<string>({
    get() {
      return routeParameter.value
    },
    set: useDebounceFn(
      (value: string) => {
        routeParameter.value = value.trim()
      },
      () => (toValue(options?.debounce) ?? 0.3) * 1000,
    ),
  })
}

export function useBooleanParameter(
  key: string,
  defaultValue = false,
  options: UsePrefixedRouteQueryOptions | undefined = undefined,
) {
  const isTrue = (value: string | null | undefined) =>
    ['true', 'on', '1'].includes((value ?? '').toLowerCase())
  const toString = (value: boolean | string) =>
    (typeof value === 'string' ? isTrue(value) : value) ? 'true' : 'false'
  return usePrefixedRouteQuery(key, toString(defaultValue), {
    ...options,
    transform: { get: isTrue, set: toString },
  })
}

export function useOrderParameter<T extends string[]>(
  key: string,
  defaultValue: T,
  options: UsePrefixedRouteQueryOptions | undefined = undefined,
) {
  const toString = (value: T) => value.join(',')
  const toArray = (value: string | null | undefined) =>
    value ? (value.split(',') as T) : defaultValue
  return usePrefixedRouteQuery<string, T>(key, defaultValue.join(','), {
    ...options,
    transform: { get: toArray, set: toString },
  })
}
